import { RadioGroup, RadioOption } from "@/components/ui/radio"
import { Select } from "@/components/ui/select"
import type {
  AutomationSchedule,
  Frequency,
  MonthlyMode,
} from "@/types/automation"

/**
 * How often an automation repeats — every day, every week on a chosen weekday,
 * or once a month on an nth weekday or a day of the month.
 *
 * Shared by the admin form, which sets the default every organization starts
 * from, and the setup screen, where a District Council changes it to suit
 * itself. It reads and writes a plain AutomationSchedule and never sees a
 * column name, because the two tables spell them differently.
 *
 * The shape follows TargetPicker: radios with the chosen one's detail revealed
 * beneath it, so the two sections of the setup page read alike.
 */

/** Index 0 is Sunday, matching Ruby's Date#wday and Date::DAYNAMES. */
const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]

/** -1 is "last", whichever week that falls in. There is deliberately no 5th. */
const ORDINALS: { value: number; label: string }[] = [
  { value: 1, label: "1st" },
  { value: 2, label: "2nd" },
  { value: 3, label: "3rd" },
  { value: 4, label: "4th" },
  { value: -1, label: "last" },
]

/**
 * The full 1–31. A day a month does not have falls back to that month's last
 * day — see the note rendered beneath the picker, and Automations::Schedule.
 */
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, index) => index + 1)

/** Above this, the chosen day is missing from at least one month of the year. */
const SHORTEST_MONTH_LENGTH = 28

function ordinalSuffix(day: number) {
  if (day % 100 >= 11 && day % 100 <= 13) return "th"
  return { 1: "st", 2: "nd", 3: "rd" }[day % 10] ?? "th"
}

type Errors = Partial<Record<keyof AutomationSchedule, string>>

export function ScheduleFields({
  value,
  onChange,
  errors = {},
  idPrefix = "schedule",
}: {
  value: AutomationSchedule
  onChange: (patch: Partial<AutomationSchedule>) => void
  errors?: Errors
  /** Keeps label/control ids unique when two of these share a page. */
  idPrefix?: string
}) {
  const id = (name: string) => `${idPrefix}_${name}`
  const error = errors.frequency ?? errors.weekday ?? errors.monthly_mode ?? errors.week_ordinal ?? errors.day_of_month

  const setFrequency = (frequency: Frequency) => onChange({ frequency })
  const setMonthlyMode = (monthly_mode: MonthlyMode) => onChange({ monthly_mode })

  return (
    <div className="space-y-3">
      <RadioGroup aria-label="How often this repeats" className="gap-2">
        <RadioOption
          name={id("frequency")}
          checked={value.frequency === "daily"}
          onChange={() => setFrequency("daily")}
          label="Every day"
          description="Once every day, at the time below."
        />

        <RadioOption
          name={id("frequency")}
          checked={value.frequency === "weekly"}
          onChange={() => setFrequency("weekly")}
          label="Every week"
        />

        {value.frequency === "weekly" && (
          <div className="flex flex-wrap items-center gap-2 pl-6">
            <label htmlFor={id("weekday")} className="text-sm font-normal text-ink-body">
              on
            </label>
            <Select
              id={id("weekday")}
              wrapperClassName="w-40"
              value={value.weekday}
              onChange={(event) => onChange({ weekday: Number(event.target.value) })}
            >
              {WEEKDAYS.map((name, index) => (
                <option key={name} value={index}>
                  {name}
                </option>
              ))}
            </Select>
          </div>
        )}

        <RadioOption
          name={id("frequency")}
          checked={value.frequency === "monthly"}
          onChange={() => setFrequency("monthly")}
          label="Once a month"
        />

        {value.frequency === "monthly" && (
          <div className="space-y-2 pl-6">
            {/* The dropdowns sit outside each option's label, so a click on
                them no longer selects the row by accident of markup. Changing
                one selects its row explicitly instead — otherwise a week or
                day could be saved under the other monthly mode. */}
            <RadioOption
              name={id("monthly_mode")}
              checked={value.monthly_mode === "nth_weekday"}
              onChange={() => setMonthlyMode("nth_weekday")}
              label="on the"
            >
              <Select
                aria-label="Which week"
                wrapperClassName="w-24"
                value={value.week_ordinal}
                onChange={(event) =>
                  onChange({
                    week_ordinal: Number(event.target.value),
                    monthly_mode: "nth_weekday",
                  })
                }
              >
                {ORDINALS.map((ordinal) => (
                  <option key={ordinal.value} value={ordinal.value}>
                    {ordinal.label}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Which day of the week"
                wrapperClassName="w-40"
                value={value.weekday}
                onChange={(event) =>
                  onChange({
                    weekday: Number(event.target.value),
                    monthly_mode: "nth_weekday",
                  })
                }
              >
                {WEEKDAYS.map((name, index) => (
                  <option key={name} value={index}>
                    {name}
                  </option>
                ))}
              </Select>
            </RadioOption>

            <RadioOption
              name={id("monthly_mode")}
              checked={value.monthly_mode === "day_of_month"}
              onChange={() => setMonthlyMode("day_of_month")}
              label="on day"
            >
              <Select
                aria-label="Which day of the month"
                wrapperClassName="w-24"
                value={value.day_of_month}
                onChange={(event) =>
                  onChange({
                    day_of_month: Number(event.target.value),
                    monthly_mode: "day_of_month",
                  })
                }
              >
                {DAYS_OF_MONTH.map((day) => (
                  <option key={day} value={day}>
                    {day}
                    {ordinalSuffix(day)}
                  </option>
                ))}
              </Select>
              <span className="text-sm text-ink-body">of the month</span>
            </RadioOption>

            {value.monthly_mode === "day_of_month" &&
              value.day_of_month > SHORTEST_MONTH_LENGTH && (
                <p className="pl-6 text-xs text-ink-muted">
                  Months without a {value.day_of_month}
                  {ordinalSuffix(value.day_of_month)} send on their last day
                  instead, so this never skips a month.
                </p>
              )}
          </div>
        )}
      </RadioGroup>

      {error && <p className="text-xs text-danger-display">{error}</p>}
    </div>
  )
}
